/**
 * ProfileSlidePanel - Premium Sliding Profile Panel
 * Features: Slide-in from right, smooth animations, full profile management
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Camera, Mail, AlertCircle, User, Settings, 
  Shield, Bell, CreditCard, LogOut, ChevronRight, Check,
  Sparkles, Crown, Edit3, Save, Moon, Sun, Monitor, Lock, Key,
  ArrowRight, RefreshCw, Loader2
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import UserProfileImage from '@/components/UserProfileImage';
import { useAuth } from '@/contexts/AuthContext';

// Helper: Suppress Codespaces environment errors
const safeSupabaseCall = async (promise) => {
  try {
    return await promise;
  } catch (err) {
    const msg = String(err?.message || err);
    if (
      msg.includes("body stream") || 
      msg.includes("Failed to execute") || 
      msg.includes("json")
    ) {
      console.warn("Supabase stream error suppressed");
      return { data: null, error: null }; 
    }
    throw err;
  }
};

const ProfileSlidePanel = ({ 
  isOpen, 
  onClose, 
  user, 
  profile, 
  subscription,
  onProfileUpdate,
  onNavigateToPricing,
  onSignOut 
}) => {
  const [activeSection, setActiveSection] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Form Data
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [emailError, setEmailError] = useState('');

  // Email Change State
  const [isEmailChangePending, setIsEmailChangePending] = useState(false);
  const [emailChangeStep, setEmailChangeStep] = useState('idle'); // 'idle' | 'validating' | 'checking' | 'sending' | 'verify'
  const [otp, setOtp] = useState('');
  const [newEmailToVerify, setNewEmailToVerify] = useState('');

  // Email validation helper
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Real-time email validation status
  const emailValidation = useMemo(() => {
    if (!email || email === user?.email) {
      return { isValid: true, message: '', showError: false };
    }
    const isValid = validateEmail(email);
    return {
      isValid,
      message: isValid ? '' : 'Please enter a valid email address',
      showError: !isValid && email.length > 3
    };
  }, [email, user?.email]);
  // Notification Preferences
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [marketingEmails, setMarketingEmails] = useState(false);
  
  // Theme from AuthContext - now persists to database
  const { theme, setTheme } = useAuth();
  
  // Password Change State
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const fileInputRef = useRef(null);
  const { toast } = useToast();

  // Track if this is a fresh open vs state update
  const prevIsOpenRef = useRef(false);
  
  useEffect(() => {
    // Only reset state on fresh panel open (was closed, now open)
    if (isOpen && !prevIsOpenRef.current) {
      setFullName(profile?.full_name || '');
      setEmail(user?.email || '');
      setAvatarUrl(profile?.avatar_url || '');
      setAvatarFile(null);
      setIsEditing(false);
      setIsEmailChangePending(false);
      setOtp('');
      setEmailError('');
      setActiveSection('profile');
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setEmailChangeStep('idle');
      setNewEmailToVerify('');
    }
    prevIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen - profile/user are read on open only
  
  // Cleanup blob URL when component unmounts or when avatarUrl changes
  useEffect(() => {
    return () => {
      // Cleanup blob URL on unmount to prevent memory leaks
      if (avatarUrl && avatarUrl.startsWith('blob:')) {
        URL.revokeObjectURL(avatarUrl);
      }
    };
  }, [avatarUrl]);
  
  // Handle file selection - called from file input onChange
  const onFileChange = useCallback((e) => {
    try {
      const file = e.target?.files?.[0];
      if (!file) return;
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        sonnerToast.error('Invalid file type', { description: 'Please select an image file' });
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        sonnerToast.error('File too large', { description: 'Please select an image under 5MB' });
        return;
      }
      
      // Create preview URL and set state
      const previewUrl = URL.createObjectURL(file);
      setAvatarFile(file);
      setAvatarUrl(previewUrl);
      sonnerToast.success('Image selected', { description: 'Click Save to upload your new profile picture' });
    } catch (error) {
      console.error('File select error:', error);
      sonnerToast.error('Failed to select image', { description: 'Please try again' });
    } finally {
      // Reset input value so same file can be selected again
      if (e.target) {
        e.target.value = '';
      }
    }
  }, []);
  
  // Trigger file picker
  const triggerFilePicker = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);
  
  // Theme Change Handler - uses AuthContext for DB persistence
  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    sonnerToast.success('Theme updated!');
  };
  
  // Password Change Handler
  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      sonnerToast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      sonnerToast.error('Password must be at least 6 characters');
      return;
    }
    
    setPasswordLoading(true);
    try {
      const { error } = await safeSupabaseCall(
        supabase.auth.updateUser({ password: newPassword })
      );
      if (error) throw error;
      
      sonnerToast.success('Password updated successfully!');
      setShowPasswordChange(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      sonnerToast.error('Failed to update password', { description: error.message });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    setLoading(true);
    setEmailError('');

    try {
      // Validate email format first
      if (email !== user.email) {
        if (!validateEmail(email)) {
          setEmailError("Please enter a valid email address");
          setLoading(false);
          return;
        }

        // Step 1: Check for duplicate email
        setEmailChangeStep('checking');
        const { data: emailExists, error: rpcError } = await safeSupabaseCall(
          supabase.rpc('check_email_exists', { email_to_check: email })
        );
        if (rpcError) throw rpcError;
        if (emailExists) {
          setEmailError("This email is already registered with another account");
          setEmailChangeStep('idle');
          setLoading(false);
          return;
        }
      }

      // Upload photo
      let uploadedAvatarUrl = avatarUrl;
      if (avatarFile) {
        try {
          const fileExt = avatarFile.name.split('.').pop()?.toLowerCase() || 'jpg';
          const fileName = `${user.id}-${Date.now()}.${fileExt}`;
          
          // Upload to Supabase storage
          const { error: uploadError } = await safeSupabaseCall(
            supabase.storage.from('avatars').upload(fileName, avatarFile, {
              cacheControl: '3600',
              upsert: false
            })
          );
          
          if (uploadError) {
            console.error('Avatar upload error:', uploadError);
            // Don't throw, just skip avatar update and continue with other changes
            sonnerToast.error('Failed to upload image', { description: 'Profile will be saved without new image' });
          } else {
            const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(fileName);
            uploadedAvatarUrl = publicUrl;
          }
        } catch (avatarError) {
          console.error('Avatar upload exception:', avatarError);
          sonnerToast.error('Failed to upload image', { description: 'Profile will be saved without new image' });
        }
      }

      // Save profile
      const updates = {
        id: user.id,
        full_name: fullName,
        avatar_url: uploadedAvatarUrl,
        updated_at: new Date().toISOString(),
      };

      const { error: profileError } = await safeSupabaseCall(
        supabase.from('profiles').upsert(updates).select()
      );
      if (profileError) throw profileError;

      // Start email change if needed
      if (email !== user.email) {
        // Step 2: Send OTP
        setEmailChangeStep('sending');
        const { error } = await safeSupabaseCall(
          supabase.auth.updateUser({ email: email })
        );
        if (error) throw error;
        setNewEmailToVerify(email);
        setIsEmailChangePending(true);
        setEmailChangeStep('verify');
        sonnerToast.success("Verification code sent!", { 
          description: `Please check ${email} for the 6-digit code` 
        });
        setLoading(false);
        return;
      }

      sonnerToast.success("Profile updated successfully!");
      if (onProfileUpdate) await onProfileUpdate();
      setIsEditing(false);
      setEmailChangeStep('idle');
      // Clear file input after successful save
      setAvatarFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error("Profile Save Error:", error);
      setEmailChangeStep('idle');
      sonnerToast.error("Error updating profile", { description: error.message || "Something went wrong" });
    } finally {
      if (!isEmailChangePending) setLoading(false);
    }
  };

  const handleVerifyEmailOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await safeSupabaseCall(
        supabase.auth.verifyOtp({
          email: newEmailToVerify,
          token: otp,
          type: 'email_change'
        })
      );
      if (error) throw error;
      sonnerToast.success("Email updated successfully!", {
        description: `Your email is now ${newEmailToVerify}`
      });
      if (onProfileUpdate) await onProfileUpdate();
      setIsEmailChangePending(false);
      setEmailChangeStep('idle');
      setOtp('');
      setIsEditing(false);
    } catch (error) {
      sonnerToast.error("Verification failed", { 
        description: error.message || "Invalid code. Please try again." 
      });
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP handler
  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const { error } = await safeSupabaseCall(
        supabase.auth.updateUser({ email: newEmailToVerify })
      );
      if (error) throw error;
      sonnerToast.success("New code sent!", { 
        description: `Check ${newEmailToVerify} for the new verification code` 
      });
    } catch (error) {
      sonnerToast.error("Failed to resend code", { description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const getPlanInfo = () => {
    const planId = subscription?.plan_id || 'free';
    const planName = subscription?.plans?.name || 'Free Tier';
    const isActive = subscription?.status === 'active';
    
    return {
      id: planId,
      name: planName,
      isActive,
      icon: planId === 'pro' ? Crown : planId === 'starter' ? Sparkles : User,
      gradient: planId === 'pro' 
        ? 'from-amber-500 to-orange-500' 
        : planId === 'starter' 
          ? 'from-violet-500 to-indigo-500' 
          : 'from-slate-500 to-slate-600',
      badgeClass: planId === 'pro'
        ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0'
        : planId === 'starter'
          ? 'bg-violet-600 text-white'
          : 'bg-slate-100 text-slate-600'
    };
  };

  const planInfo = getPlanInfo();
  const PlanIcon = planInfo.icon;

  // Safe close handler - checks for unsaved changes
  const handleSafeClose = useCallback(() => {
    // Don't close if in editing mode with unsaved changes
    if (isEditing && (avatarFile || fullName !== (profile?.full_name || '') || email !== (user?.email || ''))) {
      sonnerToast.warning('Unsaved changes', { description: 'Please save or cancel your changes first' });
      return;
    }
    onClose();
  }, [isEditing, avatarFile, fullName, email, profile?.full_name, user?.email, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Hidden File Input - positioned off-screen but accessible */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={onFileChange}
            style={{
              position: 'fixed',
              top: '-9999px',
              left: '-9999px',
              opacity: 0,
              pointerEvents: 'none'
            }}
            aria-hidden="true"
          />
          
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleSafeClose();
            }}
          />

          {/* Slide Panel */}
          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative">
              {/* Gradient Background */}
              <div className={`h-32 bg-gradient-to-br ${planInfo.gradient}`}>
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIiBjeD0iMjAiIGN5PSIyMCIgcj0iMiIvPjwvZz48L3N2Zz4=')] opacity-50" />
              </div>

              {/* Close Button */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSafeClose();
                }}
                className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors backdrop-blur-sm z-10"
              >
                <X className="w-5 h-5" />
              </motion.button>

              {/* Profile Avatar - CIRCULAR */}
              <div className="absolute -bottom-14 left-6">
                <div className="relative group">
                  <motion.div 
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="w-28 h-28 rounded-full border-4 border-white dark:border-slate-900 overflow-hidden bg-slate-100 shadow-xl"
                  >
                    <UserProfileImage 
                      src={avatarUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      iconClassName="w-12 h-12"
                    />
                  </motion.div>

                  {isEditing && !isEmailChangePending && (
                    <motion.button
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      type="button"
                      onClick={triggerFilePicker}
                      className="absolute bottom-1 right-1 p-2.5 bg-violet-600 text-white rounded-full shadow-lg hover:bg-violet-700 transition-colors z-10"
                    >
                      <Camera className="w-4 h-4" />
                    </motion.button>
                  )}
                </div>
              </div>

              {/* Plan Badge */}
              <motion.div 
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="absolute -bottom-4 right-6"
              >
                <Badge className={`${planInfo.badgeClass} px-3 py-1 text-xs font-medium shadow-lg`}>
                  <PlanIcon className="w-3 h-3 mr-1" />
                  {planInfo.name}
                </Badge>
              </motion.div>
            </div>

            {/* User Info */}
            <div className="pt-18 px-6 pb-4" style={{ paddingTop: '4.5rem' }}>
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.15 }}
              >
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  {profile?.full_name || 'User'}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  {user?.email}
                </p>
              </motion.div>
            </div>

            {/* Navigation Tabs - Vertical Layout */}
            <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-800">
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'profile', label: 'Profile', icon: User },
                  { id: 'security', label: 'Security', icon: Shield },
                  { id: 'theme', label: 'Theme', icon: Moon },
                  { id: 'account', label: 'Account', icon: Settings },
                  { id: 'notifications', label: 'Alerts', icon: Bell }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveSection(tab.id)}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                      activeSection === tab.id
                        ? 'bg-violet-100 dark:bg-violet-900/50 text-violet-600 dark:text-violet-400'
                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <AnimatePresence mode="wait">
                {/* Profile Section */}
                {activeSection === 'profile' && (
                  <motion.div
                    key="profile"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-6"
                  >
                    {isEmailChangePending ? (
                      // Branded OTP Verification Flow
                      <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6 py-4"
                      >
                        {/* Progress Steps */}
                        <div className="flex items-center justify-center gap-2">
                          <div className="flex items-center gap-1">
                            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                              <Check className="w-3.5 h-3.5 text-white" />
                            </div>
                            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Email Valid</span>
                          </div>
                          <div className="w-8 h-0.5 bg-emerald-500" />
                          <div className="flex items-center gap-1">
                            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                              <Check className="w-3.5 h-3.5 text-white" />
                            </div>
                            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Code Sent</span>
                          </div>
                          <div className="w-8 h-0.5 bg-violet-500" />
                          <div className="flex items-center gap-1">
                            <div className="w-6 h-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 flex items-center justify-center animate-pulse">
                              <span className="text-[10px] font-bold text-white">3</span>
                            </div>
                            <span className="text-xs text-violet-600 dark:text-violet-400 font-medium">Verify</span>
                          </div>
                        </div>

                        {/* Icon */}
                        <motion.div
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                          className="w-20 h-20 mx-auto bg-gradient-to-br from-violet-100 to-indigo-100 dark:from-violet-900/30 dark:to-indigo-900/30 rounded-2xl flex items-center justify-center shadow-lg"
                        >
                          <Mail className="w-10 h-10 text-violet-600 dark:text-violet-400" />
                        </motion.div>

                        {/* Text */}
                        <div className="text-center space-y-2">
                          <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                            Verify Your Email
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            We've sent a 6-digit verification code to
                          </p>
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-50 dark:bg-violet-900/30 rounded-xl">
                            <Mail className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                            <span className="text-sm font-semibold text-violet-600 dark:text-violet-400">
                              {newEmailToVerify}
                            </span>
                          </div>
                        </div>

                        {/* OTP Input */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 block text-center">
                            Enter Verification Code
                          </Label>
                          <div className="relative">
                            <Input
                              placeholder="• • • • • •"
                              className="text-center text-2xl tracking-[0.5em] h-14 font-mono bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 focus:border-violet-500 dark:focus:border-violet-500"
                              maxLength={6}
                              value={otp}
                              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                              autoFocus
                            />
                            {otp.length === 6 && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="absolute right-3 top-1/2 -translate-y-1/2"
                              >
                                <Check className="w-5 h-5 text-emerald-500" />
                              </motion.div>
                            )}
                          </div>
                        </div>

                        {/* Verify Button */}
                        <Button
                          onClick={handleVerifyEmailOtp}
                          className="w-full h-12 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 transition-all"
                          disabled={loading || otp.length < 6}
                        >
                          {loading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Verify & Update Email
                            </>
                          )}
                        </Button>

                        {/* Resend & Cancel */}
                        <div className="flex items-center justify-center gap-4">
                          <button
                            onClick={handleResendOtp}
                            disabled={loading}
                            className="text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 font-medium flex items-center gap-1 transition-colors disabled:opacity-50"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Resend Code
                          </button>
                          <span className="text-slate-300 dark:text-slate-600">|</span>
                          <button
                            onClick={() => {
                              setIsEmailChangePending(false);
                              setEmailChangeStep('idle');
                              setOtp('');
                              setEmail(user?.email || '');
                            }}
                            className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 font-medium transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </motion.div>
                    ) : !isEditing ? (
                      // View Mode
                      <div className="space-y-4">
                        <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500 dark:text-slate-400">Full Name</span>
                            <span className="text-sm font-medium text-slate-900 dark:text-white">
                              {fullName || 'Not set'}
                            </span>
                          </div>
                          <Separator />
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500 dark:text-slate-400">Email</span>
                            <span className="text-sm font-medium text-slate-900 dark:text-white">
                              {email}
                            </span>
                          </div>
                          <Separator />
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-slate-500 dark:text-slate-400">Account ID</span>
                            <span className="text-xs font-mono text-slate-500 dark:text-slate-400">
                              {user?.id?.slice(0, 8)}...
                            </span>
                          </div>
                        </div>

                        <Button
                          onClick={() => setIsEditing(true)}
                          variant="outline"
                          className="w-full h-11 gap-2"
                        >
                          <Edit3 className="w-4 h-4" />
                          Edit Profile
                        </Button>
                      </div>
                    ) : (
                      // Edit Mode
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Full Name</Label>
                          <Input
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            placeholder="Enter your name"
                            className="h-11"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Email Address</Label>
                          <div className="relative">
                            <Input
                              value={email}
                              onChange={(e) => {
                                setEmail(e.target.value);
                                setEmailError('');
                                setEmailChangeStep('idle');
                              }}
                              placeholder="Enter your email"
                              className={`h-11 pr-10 ${
                                emailError || emailValidation.showError 
                                  ? 'border-red-500 focus-visible:ring-red-500 border-b-2 border-b-red-500' 
                                  : email !== user?.email && emailValidation.isValid
                                    ? 'border-emerald-500 focus-visible:ring-emerald-500'
                                    : ''
                              }`}
                            />
                            {/* Validation Icon */}
                            {email !== user?.email && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                {emailValidation.isValid ? (
                                  <Check className="w-4 h-4 text-emerald-500" />
                                ) : emailValidation.showError ? (
                                  <AlertCircle className="w-4 h-4 text-red-500" />
                                ) : null}
                              </div>
                            )}
                          </div>

                          {/* Error Messages */}
                          <AnimatePresence mode="wait">
                            {emailValidation.showError && (
                              <motion.p 
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -5 }}
                                className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1.5"
                              >
                                <AlertCircle className="w-3.5 h-3.5" />
                                {emailValidation.message}
                              </motion.p>
                            )}
                            {emailError && (
                              <motion.p 
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -5 }}
                                className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1.5"
                              >
                                <AlertCircle className="w-3.5 h-3.5" />
                                {emailError}
                              </motion.p>
                            )}
                          </AnimatePresence>

                          {/* Email Change Info */}
                          {!emailError && !emailValidation.showError && email !== user?.email && emailValidation.isValid && (
                            <motion.div 
                              initial={{ opacity: 0, y: -5 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="p-3 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800"
                            >
                              <div className="flex items-start gap-2">
                                <Mail className="w-4 h-4 text-violet-600 dark:text-violet-400 mt-0.5 flex-shrink-0" />
                                <div className="space-y-1">
                                  <p className="text-sm font-medium text-violet-700 dark:text-violet-300">
                                    Email Change Process
                                  </p>
                                  <p className="text-xs text-violet-600 dark:text-violet-400">
                                    We'll verify availability and send a 6-digit code to your new email for verification.
                                  </p>
                                </div>
                              </div>

                              {/* Step Indicator when processing */}
                              {(emailChangeStep === 'checking' || emailChangeStep === 'sending') && (
                                <motion.div 
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  className="mt-3 pt-3 border-t border-violet-200 dark:border-violet-800"
                                >
                                  <div className="flex items-center gap-3">
                                    <Loader2 className="w-4 h-4 animate-spin text-violet-600" />
                                    <span className="text-sm font-medium text-violet-600 dark:text-violet-400">
                                      {emailChangeStep === 'checking' 
                                        ? 'Checking email availability...' 
                                        : 'Sending verification code...'}
                                    </span>
                                  </div>
                                </motion.div>
                              )}
                            </motion.div>
                          )}
                        </div>

                        <div className="flex gap-3 pt-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setIsEditing(false);
                              setFullName(profile?.full_name || '');
                              setEmail(user?.email || '');
                              setAvatarUrl(profile?.avatar_url || '');
                              setAvatarFile(null);
                              setEmailError('');
                              setEmailChangeStep('idle');
                            }}
                            className="flex-1 h-11"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleSaveChanges}
                            disabled={loading || emailValidation.showError}
                            className="flex-1 h-11 bg-gradient-to-r from-violet-600 to-indigo-600 text-white"
                          >
                            {loading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : email !== user?.email ? (
                              <>
                                <ArrowRight className="w-4 h-4 mr-2" />
                                Continue
                              </>
                            ) : (
                              <>
                                <Save className="w-4 h-4 mr-2" />
                                Save
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Security Section */}
                {activeSection === 'security' && (
                  <motion.div
                    key="security"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-4"
                  >
                    {!showPasswordChange ? (
                      <>
                        <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                                <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-900 dark:text-white">
                                  Email Verified
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                  {user?.email}
                                </p>
                              </div>
                            </div>
                            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400">
                              Verified
                            </Badge>
                          </div>
                        </div>

                        <button
                          onClick={() => setShowPasswordChange(true)}
                          className="w-full flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/50 flex items-center justify-center">
                              <Key className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                            </div>
                            <div className="text-left">
                              <p className="text-sm font-medium text-slate-900 dark:text-white">
                                Change Password
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                Update your account password
                              </p>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-violet-500 group-hover:translate-x-1 transition-all" />
                        </button>

                        <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200/50 dark:border-blue-800/50">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
                              <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                Account Security
                              </p>
                              <p className="text-xs text-blue-700/80 dark:text-blue-300/80 mt-1">
                                Your account is protected. We recommend using a strong, unique password.
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 mb-4">
                          <button
                            onClick={() => setShowPasswordChange(false)}
                            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                          >
                            <ChevronRight className="w-5 h-5 rotate-180" />
                          </button>
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                            Change Password
                          </h3>
                        </div>

                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">New Password</Label>
                            <Input
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Enter new password"
                              className="h-11"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Confirm Password</Label>
                            <Input
                              type="password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              placeholder="Confirm new password"
                              className="h-11"
                            />
                          </div>
                        </div>

                        <div className="flex gap-3 pt-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setShowPasswordChange(false);
                              setNewPassword('');
                              setConfirmPassword('');
                            }}
                            className="flex-1 h-11"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handlePasswordChange}
                            disabled={passwordLoading || !newPassword || !confirmPassword}
                            className="flex-1 h-11 bg-gradient-to-r from-violet-600 to-indigo-600 text-white"
                          >
                            {passwordLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Lock className="w-4 h-4 mr-2" />
                                Update
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Theme Section */}
                {activeSection === 'theme' && (
                  <motion.div
                    key="theme"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-4"
                  >
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-slate-900 dark:text-white mb-4">
                        Choose your preferred theme
                      </p>
                      
                      {[
                        { id: 'light', label: 'Light', description: 'Classic bright theme', icon: Sun, color: 'from-amber-400 to-orange-400' },
                        { id: 'dark', label: 'Dark', description: 'Easy on the eyes', icon: Moon, color: 'from-slate-600 to-slate-800' },
                        { id: 'system', label: 'System', description: 'Follow system settings', icon: Monitor, color: 'from-violet-500 to-indigo-500' },
                      ].map((themeOption) => (
                        <motion.button
                          key={themeOption.id}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => handleThemeChange(themeOption.id)}
                          className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                            theme === themeOption.id
                              ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                              : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 hover:border-violet-300 dark:hover:border-violet-700'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${themeOption.color} flex items-center justify-center`}>
                              <themeOption.icon className="w-5 h-5 text-white" />
                            </div>
                            <div className="text-left">
                              <p className="text-sm font-medium text-slate-900 dark:text-white">
                                {themeOption.label}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {themeOption.description}
                              </p>
                            </div>
                          </div>
                          {theme === themeOption.id && (
                            <div className="w-6 h-6 rounded-full bg-violet-500 flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </motion.button>
                      ))}
                    </div>

                    <div className="p-4 rounded-xl bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 border border-violet-200/50 dark:border-violet-800/50">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/50 flex items-center justify-center flex-shrink-0">
                          <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-violet-900 dark:text-violet-100">
                            Theme Tips
                          </p>
                          <p className="text-xs text-violet-700/80 dark:text-violet-300/80 mt-1">
                            Dark mode is great for low-light conditions. System mode automatically switches based on your device settings.
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Account Section */}
                {activeSection === 'account' && (
                  <motion.div
                    key="account"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-4"
                  >
                    {/* Current Plan */}
                    <div className={`p-4 rounded-xl bg-gradient-to-br ${planInfo.gradient} text-white`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <PlanIcon className="w-5 h-5" />
                          <span className="font-semibold">{planInfo.name}</span>
                        </div>
                        <Badge className="bg-white/20 text-white border-0">
                          {planInfo.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      {subscription?.current_period_end && (
                        <p className="text-sm text-white/80">
                          {subscription.cancel_at_period_end 
                            ? `Cancels on ${new Date(subscription.current_period_end).toLocaleDateString()}`
                            : `Renews on ${new Date(subscription.current_period_end).toLocaleDateString()}`
                          }
                        </p>
                      )}
                    </div>

                    {/* Billing Action */}
                    <button
                      onClick={async () => {
                        if (planInfo.id === 'free') {
                          // Free users go to pricing page
                          onClose();
                          onNavigateToPricing?.();
                        } else {
                          // Paid users go to customer portal
                          try {
                            sonnerToast.loading('Opening subscription portal...');
                            
                            const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
                            
                            if (sessionError || !sessionData?.session?.access_token) {
                              sonnerToast.dismiss();
                              sonnerToast.error('Session expired. Please log in again.');
                              return;
                            }
                            
                            const accessToken = sessionData.session.access_token;
                            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 
                                               process.env.REACT_APP_API_URL || 
                                               'https://trust-flow-app.vercel.app';
                            
                            const response = await fetch(`${BACKEND_URL}/api/create-portal-session`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${accessToken}`,
                              },
                              body: JSON.stringify({ user_id: user?.id }),
                            });
                            
                            const data = await response.json();
                            sonnerToast.dismiss();
                            
                            if (!response.ok) {
                              // Handle specific errors
                              if (response.status === 401) {
                                sonnerToast.error('Session expired. Please log in again.');
                                return;
                              }
                              if (response.status === 403) {
                                sonnerToast.error('Access denied.');
                                return;
                              }
                              sonnerToast.error(data.detail || 'Unable to open billing portal');
                              return;
                            }
                            
                            if (data.url) {
                              window.location.href = data.url;
                            } else {
                              sonnerToast.error('Unable to open billing portal. Please try from dashboard.');
                            }
                          } catch (error) {
                            console.error('Portal error:', error);
                            sonnerToast.dismiss();
                            sonnerToast.error('Unable to open billing portal. Please check your connection.');
                          }
                        }
                      }}
                      className="w-full flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/50 flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-medium text-slate-900 dark:text-white">
                            {planInfo.id === 'free' ? 'Upgrade Plan' : 'Manage Subscription'}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {planInfo.id === 'free' ? 'Get more features' : 'View billing & invoices'}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-violet-500 group-hover:translate-x-1 transition-all" />
                    </button>

                    <Separator className="my-4" />

                    {/* Danger Zone */}
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Danger Zone
                      </p>
                      <button
                        onClick={onSignOut}
                        className="w-full flex items-center justify-between p-4 rounded-xl bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                            <LogOut className="w-5 h-5 text-red-600 dark:text-red-400" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-red-600 dark:text-red-400">
                              Sign Out
                            </p>
                            <p className="text-xs text-red-500/70 dark:text-red-400/70">
                              Log out of your account
                            </p>
                          </div>
                        </div>
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Notifications Section */}
                {activeSection === 'notifications' && (
                  <motion.div
                    key="notifications"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-4"
                  >
                    {/* Under Maintenance Banner */}
                    <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-800/50">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
                          <Settings className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                            Under Maintenance
                          </p>
                          <p className="text-xs text-amber-700/80 dark:text-amber-300/80 mt-1">
                            Notification preferences are currently being updated. Please check back soon.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 space-y-4 opacity-50 pointer-events-none">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">
                            Email Notifications
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Get notified about new testimonials
                          </p>
                        </div>
                        <Switch
                          checked={emailNotifications}
                          onCheckedChange={setEmailNotifications}
                          disabled
                        />
                      </div>
                      <Separator />
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">
                            Marketing Emails
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Product updates & tips
                          </p>
                        </div>
                        <Switch
                          checked={marketingEmails}
                          onCheckedChange={setMarketingEmails}
                          disabled
                        />
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-violet-50 dark:bg-violet-900/20 border border-violet-200/50 dark:border-violet-800/50">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/50 flex items-center justify-center flex-shrink-0">
                          <Bell className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-violet-900 dark:text-violet-100">
                            Stay Updated
                          </p>
                          <p className="text-xs text-violet-700/80 dark:text-violet-300/80 mt-1">
                            We'll notify you when someone submits a new testimonial to your spaces.
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
              <p className="text-xs text-center text-slate-400 dark:text-slate-500">
                TrustWall • Your testimonials, your way
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ProfileSlidePanel;
